import { createFileRoute, useNavigate, getRouteApi } from "@tanstack/react-router";
import type { CurrentUser } from "../../../lib/auth";
const _authedRoute = getRouteApi("/_authed");
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { api } from "../../../lib/api";
import { useClickOutside } from "../../../lib/useClickOutside";
import { TabGeral } from "../../../components/agent/TabGeral";
import { TabConexao } from "../../../components/agent/TabConexao";
import { TabConfig } from "../../../components/agent/TabConfig";
import { UnsavedGuard, type LeaveController } from "../../../components/agent/unsavedGuard";
import { TabConversas } from "../../../components/agent/TabConversas";
import { TabExecucoes } from "../../../components/agent/TabExecucoes";
import { TabPlayground } from "../../../components/agent/TabPlayground";
import { DialogPanel } from "../../../components/shared/DialogPanel";
import { AlertTriangleIcon, UsersIcon, TransferIcon } from "../../../components/shared/Icons";
import { TabMetricas } from "../../../components/agent/TabMetricas";
import { TabInsights } from "../../../components/agent/TabInsights";
// N8N-IMPORT: aba provisória — remover junto com a feature de importação.
import { TabImportN8n } from "../../../components/agent/TabImportN8n";
import { backdropClose } from "../../../components/shared/backdrop";

function presenceSessionId(): string {
  try {
    const k = "agent-presence-session";
    let id = sessionStorage.getItem(k);
    if (!id) { id = crypto.randomUUID(); sessionStorage.setItem(k, id); }
    return id;
  } catch { return crypto.randomUUID(); } // sessionStorage bloqueado: cai no comportamento antigo
}

export const Route = createFileRoute("/_authed/agents/$agentId")({
  component: AgentDetailPage,
});

/** "Eduardo Sebastiany" → "Eduardo S." */
function abbrevName(full: string): string {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

// ids históricos: "configuracoes" é a Workstation (prompt/modelo/tools...)
// e "geral" virou a aba Configurações (dados gerais do agente).
const TABS = [
  { id: "configuracoes", label: "Workstation" },
  { id: "execucoes", label: "Execuções" },
  { id: "playground", label: "Playground" },
  { id: "conversas", label: "Conversas" },
  { id: "conexao", label: "Conexão" },
  { id: "metricas", label: "Métricas" },
  { id: "insights", label: "Insights" },
  { id: "geral", label: "Configurações" },
  // N8N-IMPORT: aba provisória de migração — remover junto com a feature.
  { id: "importn8n", label: "Importar do n8n" },
] as const;

type TabId = (typeof TABS)[number]["id"];

type ConfirmAction = "publish" | "deactivate" | "reactivate" | null;
type DeleteStep = 0 | 1 | 2;
type AccessMode = null | "choose" | "participants" | "transfer-search" | "transfer-confirm";

const STATUS_LABEL: Record<string, string> = {
  development: "Em desenvolvimento",
  active: "Ativo",
  inactive: "Inativo",
};

const STATUS_STYLE: Record<string, { bg: string; color: string; border: string }> = {
  development: { bg: "var(--info-soft)", color: "var(--info)", border: "var(--info-line)" },
  active:      { bg: "var(--ok-soft)", color: "var(--ok)", border: "var(--ok-line)" },
  inactive:    { bg: "var(--surface-card)", color: "var(--ink-muted)", border: "var(--line-strong)"   },
};

function AgentDetailPage() {
  const { agentId } = Route.useParams();
  const navigate = useNavigate();
  const routeCtx = _authedRoute.useRouteContext() as { user?: CurrentUser };
  const currentUser = routeCtx.user;
  const isAdmin = currentUser?.role_name === "Admin";
  const canDelete = isAdmin || !!currentUser?.permissions?.agente_excluir;
  const canManageParticipants = isAdmin || !!currentUser?.permissions?.agente_participantes;
  const canChangeOwner = isAdmin || !!currentUser?.permissions?.agente_alterar_proprietario;
  const isViewer = !!currentUser?.permissions?.viewer;
  // Viewer só enxerga estas abas; o resto fica oculto.
  const visibleTabs = isViewer
    ? TABS.filter((t) => t.id === "geral" || t.id === "metricas" || t.id === "conversas")
    : TABS;
  // Workstation é a aba de entrada; viewer não a enxerga — cai na primeira visível.
  const [activeTab, setActiveTab] = useState<TabId>(isViewer ? "conversas" : "configuracoes");
  const [confirm, setConfirm] = useState<ConfirmAction>(null);
  const [deleteStep, setDeleteStep] = useState<DeleteStep>(0);
  const [accessMode, setAccessMode] = useState<AccessMode>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [participantSearch, setParticipantSearch] = useState("");
  const [transferSearch, setTransferSearch] = useState("");
  const participantSearchRef = useRef<HTMLDivElement>(null);
  const transferSearchRef = useRef<HTMLDivElement>(null);
  useClickOutside(participantSearchRef, participantSearch.length >= 2, () => setParticipantSearch(""));
  useClickOutside(transferSearchRef, transferSearch.length >= 2, () => setTransferSearch(""));
  const [transferTarget, setTransferTarget] = useState<{ id: string; name: string } | null>(null);
  const [showTagPopup, setShowTagPopup] = useState(false);
  const [tagSearch, setTagSearch] = useState("");
  const [showChangelog, setShowChangelog] = useState(false);
  const [changelog, setChangelog] = useState("");
  const qc = useQueryClient();
  // Aviso de alterações não salvas (aba Configurações) ao sair da aba ou da página.
  const guardCtl = useRef<LeaveController>({ attemptLeave: (run) => run() });

  const { data: agent, isLoading } = useQuery<any>({
    queryKey: ["agent", agentId],
    queryFn: () => api.get(`/agents/${agentId}`),
  });

  // Título da aba: "Valq - {agente}" aqui dentro; volta ao padrão ao sair.
  useEffect(() => {
    if (agent?.name) document.title = `Valq - ${agent.name}`;
    return () => { document.title = "Valquíria"; };
  }, [agent?.name]);

  const statusMutation = useMutation({
    mutationFn: (newStatus: string) =>
      api.patch(`/agents/${agentId}`, { status: newStatus }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent", agentId] });
      qc.invalidateQueries({ queryKey: ["agents"] });
      qc.invalidateQueries({ queryKey: ["dashboard-projects"] });
      setConfirm(null);
    },
  });

  const publishMutation = useMutation({
    mutationFn: (payload: { changelog?: string }) => api.post(`/agents/${agentId}/publish`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent", agentId] });
      qc.invalidateQueries({ queryKey: ["agents"] });
      qc.invalidateQueries({ queryKey: ["dashboard-projects"] });
      qc.invalidateQueries({ queryKey: ["agent-publications", agentId] });
      setConfirm(null);
      setShowChangelog(false);
      setChangelog("");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/agents/${agentId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agents"] });
      qc.invalidateQueries({ queryKey: ["dashboard-projects"] });
      navigate({ to: "/agents" });
    },
  });

  const addParticipantMutation = useMutation({
    mutationFn: (userId: string) =>
      api.post(`/agents/${agentId}/participants`, { user_id: userId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent", agentId] });
      setParticipantSearch("");
    },
  });

  const removeParticipantMutation = useMutation({
    mutationFn: (userId: string) =>
      api.delete(`/agents/${agentId}/participants/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent", agentId] });
      setConfirmRemoveId(null);
    },
  });

  const transferOwnerMutation = useMutation({
    mutationFn: (newManagerId: string) =>
      api.patch(`/agents/${agentId}/transfer-owner`, { new_manager_id: newManagerId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent", agentId] });
      qc.invalidateQueries({ queryKey: ["agents"] });
      setAccessMode(null);
      setTransferSearch("");
      setTransferTarget(null);
    },
  });

  const { data: allTags = [] } = useQuery<{ tag: string; count: number }[]>({
    queryKey: ["agent-tags-global"],
    queryFn: () => api.get("/agents/tags"),
    staleTime: 30_000,
  });

  const updateTagsMutation = useMutation({
    mutationFn: (tags: string[]) => api.patch(`/agents/${agentId}`, { tags }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent", agentId] }),
  });

  const { data: searchResults } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["participant-search", participantSearch],
    queryFn: () => api.get(`/agents/participants/search?q=${encodeURIComponent(participantSearch)}`),
    enabled: participantSearch.length >= 2,
    staleTime: 10_000,
  });

  const { data: transferSearchResults } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["transfer-search", transferSearch],
    queryFn: () => api.get(`/agents/participants/search?q=${encodeURIComponent(transferSearch)}`),
    enabled: transferSearch.length >= 2,
    staleTime: 10_000,
  });

  // Presença: aviso "aberto em outra aba" (estilo n8n). Session id único por aba;
  // heartbeat 15s / TTL 45s no backend — no load da página o aviso já vem na hora.
  // Vive no sessionStorage (por aba, sobrevive ao F5): senão o reload gerava id
  // novo e o antigo ficava "vivo" no Valkey por até 45s → falso "outra aba".
  const presenceSession = useRef(presenceSessionId());
  const [presenceDismissed, setPresenceDismissed] = useState(false);
  const { data: presence } = useQuery<{ others: { name: string; same_user: boolean }[] }>({
    queryKey: ["agent-presence", agentId],
    queryFn: () => api.post(`/agents/${agentId}/presence`, { session: presenceSession.current }),
    refetchInterval: 15_000,
    refetchIntervalInBackground: true, // aba em segundo plano continua "aberta"
  });
  const presenceOthers = presence?.others ?? [];

  if (isLoading) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--ink-muted)",
          fontSize: 13,
        }}
      >
        Carregando...
      </div>
    );
  }

  if (!agent) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--ink-muted)",
          fontSize: 13,
        }}
      >
        Agente não encontrado.
      </div>
    );
  }

  const status: string = agent.status ?? "development";
  const prevStatus: string | null = agent.previous_status ?? null;
  const styleStatus = STATUS_STYLE[status] ?? STATUS_STYLE.inactive;
  const dirty = !!agent.has_unpublished_changes;
  const publishedVersion: string | null = agent.published_version ?? null;

  const CONFIRM_COPY: Record<NonNullable<ConfirmAction>, { title: string; body: string; label: string; color: string }> = {
    publish:    { title: "Publicar agente",   body: "O agente passará para Ativo e será publicado como versão 1.0. A partir daí, novas alterações só vão ao ar quando você publicar.", label: "Publicar",  color: "var(--ok)" },
    deactivate: { title: "Desativar agente",  body: "O agente ficará inativo e não processará novas mensagens.",                                                      label: "Desativar", color: "var(--danger)" },
    reactivate: { title: "Reativar agente",   body: `O agente voltará para o status "${STATUS_LABEL[prevStatus ?? ""] ?? prevStatus}".`,                             label: "Reativar",  color: "var(--ok)" },
  };

  function handleConfirm() {
    if (!confirm) return;
    if (confirm === "publish")    publishMutation.mutate({});
    if (confirm === "deactivate") statusMutation.mutate("inactive");
    if (confirm === "reactivate") statusMutation.mutate(prevStatus ?? "active");
  }

  return (
    <UnsavedGuard controllerRef={guardCtl}>
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0, height: "100%" }}>

      {/* Modal de confirmação */}
      {confirm && (
        <div {...backdropClose(() => { setConfirm(null); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setConfirm(null)} role="alertdialog" label={CONFIRM_COPY[confirm].title} style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 28, width: 380, display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{CONFIRM_COPY[confirm].title}</div>
            <div style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.5 }}>{CONFIRM_COPY[confirm].body}</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirm(null)} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>
                Cancelar
              </button>
              {(() => {
                const pending = statusMutation.isPending || publishMutation.isPending;
                return (
                  <button onClick={handleConfirm} disabled={pending} style={{ padding: "7px 16px", background: CONFIRM_COPY[confirm].color, border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: pending ? 0.6 : 1 }}>
                    {pending ? "Salvando..." : CONFIRM_COPY[confirm].label}
                  </button>
                );
              })()}
            </div>
          </DialogPanel>
        </div>
      )}

      {/* Modal de changelog (publicar nova versão) */}
      {showChangelog && (
        <div {...backdropClose(() => { setShowChangelog(false); setChangelog(""); publishMutation.reset(); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => { setShowChangelog(false); setChangelog(""); publishMutation.reset(); }} label="Publicar nova versão" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 28, width: 440, display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Publicar nova versão</div>
            <div style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.5 }}>Descreva o que mudou nesta versão (mínimo 30 caracteres).</div>
            <textarea
              rows={5}
              value={changelog}
              onChange={(e) => setChangelog(e.target.value)}
              placeholder="Ex: Ajustei o tom do agente e adicionei a ferramenta de agendamento..."
              style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", background: "var(--surface-raised)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }}
            />
            <div style={{ fontSize: 12, alignSelf: "flex-end", color: changelog.trim().length < 30 ? "var(--danger)" : "var(--ok)" }}>
              {changelog.trim().length}/30
            </div>
            {publishMutation.isError && (
              <span style={{ fontSize: 12, color: "var(--danger)" }}>
                {(publishMutation.error as any)?.payload?.detail ?? "Erro ao publicar. Tente novamente."}
              </span>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => { setShowChangelog(false); setChangelog(""); publishMutation.reset(); }} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>
                Cancelar
              </button>
              <button
                onClick={() => publishMutation.mutate({ changelog })}
                disabled={changelog.trim().length < 30 || publishMutation.isPending}
                style={{ padding: "7px 16px", background: "var(--ok)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: changelog.trim().length < 30 || publishMutation.isPending ? "default" : "pointer", opacity: changelog.trim().length < 30 || publishMutation.isPending ? 0.6 : 1 }}
              >
                {publishMutation.isPending ? "Publicando..." : "Publicar"}
              </button>
            </div>
          </DialogPanel>
        </div>
      )}

      {/* Delete passo 1 */}
      {deleteStep === 1 && (
        <div {...backdropClose(() => { setDeleteStep(0); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setDeleteStep(0)} role="alertdialog" label="Excluir agente?" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 28, width: 380, display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Excluir agente?</div>
            <div style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.5 }}>
              Deseja excluir o agente <strong style={{ color: "var(--ink)" }}>{agent.name}</strong>?
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteStep(0)} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
              <button onClick={() => setDeleteStep(2)} style={{ padding: "7px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Avançar</button>
            </div>
          </DialogPanel>
        </div>
      )}

      {/* Delete passo 2 */}
      {deleteStep === 2 && (
        <div {...backdropClose(() => { setDeleteStep(0); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setDeleteStep(0)} role="alertdialog" label="Ação irreversível" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 28, width: 380, display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 15, color: "var(--danger)" }}>Ação irreversível</div>
            <div style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.5 }}>
              Essa ação é irreversível e resultará na exclusão total do agente. Deseja continuar mesmo assim?
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteStep(0)} disabled={deleteMutation.isPending} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
              <button onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending} style={{ padding: "7px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: deleteMutation.isPending ? 0.6 : 1 }}>
                {deleteMutation.isPending ? "Excluindo..." : "Sim, excluir"}
              </button>
            </div>
          </DialogPanel>
        </div>
      )}

      {/* Unified access popup */}
      {accessMode !== null && (
        <div {...backdropClose(() => { setAccessMode(null); setConfirmRemoveId(null); setParticipantSearch(""); setTransferSearch(""); setTransferTarget(null); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => { setAccessMode(null); setConfirmRemoveId(null); setParticipantSearch(""); setTransferSearch(""); setTransferTarget(null); }} label={accessMode === "choose" ? "Gerenciar Acessos" : accessMode === "participants" ? "Adicionar participantes" : accessMode === "transfer-search" ? "Trocar proprietário" : "Confirmar transferência"} style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 380, display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>
                {accessMode === "choose" ? "Gerenciar Acessos" :
                 accessMode === "participants" ? "Adicionar participantes" :
                 accessMode === "transfer-search" ? "Trocar proprietário" :
                 "Confirmar transferência"}
              </span>
              <button
                onClick={() => { setAccessMode(null); setConfirmRemoveId(null); setParticipantSearch(""); setTransferSearch(""); setTransferTarget(null); }}
                style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 18, lineHeight: 1 }}
              >×</button>
            </div>

            {/* Choose screen */}
            {accessMode === "choose" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {canManageParticipants && (
                  <button
                    onClick={() => setAccessMode("participants")}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 10, color: "var(--ink)", fontSize: 13, cursor: "pointer", textAlign: "left", width: "100%" }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--brand)")}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--line)")}
                  >
                    <UsersIcon size={18} />
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>Adicionar participantes</div>
                      <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Dá acesso a outros gestores neste agente</div>
                    </div>
                  </button>
                )}
                {canChangeOwner && (
                  <button
                    onClick={() => setAccessMode("transfer-search")}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 10, color: "var(--ink)", fontSize: 13, cursor: "pointer", textAlign: "left", width: "100%" }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--brand)")}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--line)")}
                  >
                    <TransferIcon size={18} />
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>Trocar proprietário</div>
                      <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Transfere a propriedade deste agente para outro gestor</div>
                    </div>
                  </button>
                )}
              </div>
            )}

            {/* Participants screen */}
            {accessMode === "participants" && (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", borderRadius: 8, background: "var(--danger-soft)" }}>
                    <span style={{ fontSize: 13, color: "var(--danger)" }}>{agent.manager_name}</span>
                    <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>owner</span>
                  </div>
                  {(agent.participants ?? []).map((p: { id: string; name: string }) => (
                    <div key={p.id} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", borderRadius: 8, background: "var(--warn-soft)" }}>
                        <span style={{ fontSize: 13, color: "var(--warn)" }}>{p.name}</span>
                        <button onClick={() => setConfirmRemoveId(confirmRemoveId === p.id ? null : p.id)} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
                      </div>
                      {confirmRemoveId === p.id && (
                        <div style={{ padding: "8px 10px", background: "var(--surface-card)", borderRadius: 8, border: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: 8 }}>
                          <span style={{ fontSize: 12, color: "var(--ink-mid)" }}>Remover <strong style={{ color: "var(--ink)" }}>{p.name}</strong> dos participantes?</span>
                          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button onClick={() => setConfirmRemoveId(null)} style={{ padding: "4px 12px", background: "var(--line)", border: "none", borderRadius: 6, color: "var(--ink-secondary)", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
                            <button onClick={() => removeParticipantMutation.mutate(p.id)} disabled={removeParticipantMutation.isPending} style={{ padding: "4px 12px", background: "var(--danger-soft)", border: "none", borderRadius: 6, color: "var(--danger)", fontSize: 12, fontWeight: 600, cursor: "pointer", opacity: removeParticipantMutation.isPending ? 0.6 : 1 }}>Remover</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div style={{ borderTop: "1px solid var(--line)" }} />

                <div ref={participantSearchRef} style={{ position: "relative" }}>
                  <input
                    value={participantSearch}
                    onChange={(e) => setParticipantSearch(e.target.value)}
                    placeholder="Buscar gestor por nome..."
                    style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", background: "var(--surface-raised)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none" }}
                  />
                  {searchResults && searchResults.length > 0 && participantSearch.length >= 2 && (
                    <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", zIndex: 10 }}>
                      {searchResults
                        .filter(u => u.id !== agent.manager_id && !(agent.participants ?? []).some((p: { id: string }) => p.id === u.id))
                        .map(u => (
                          <button key={u.id} onClick={() => addParticipantMutation.mutate(u.id)}
                            style={{ width: "100%", textAlign: "left", padding: "8px 12px", background: "none", border: "none", color: "var(--ink)", fontSize: 13, cursor: "pointer" }}
                            onMouseEnter={e => (e.currentTarget.style.background = "var(--line)")}
                            onMouseLeave={e => (e.currentTarget.style.background = "none")}
                          >{u.name}</button>
                        ))
                      }
                    </div>
                  )}
                </div>

                {canChangeOwner && (
                  <button onClick={() => { setAccessMode("choose"); setConfirmRemoveId(null); setParticipantSearch(""); }} style={{ alignSelf: "flex-start", background: "none", border: "none", color: "var(--ink-muted)", fontSize: 12, cursor: "pointer", padding: 0 }}>
                    ← Voltar
                  </button>
                )}
              </>
            )}

            {/* Transfer search screen */}
            {accessMode === "transfer-search" && (
              <>
                <div style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.5 }}>
                  Proprietário atual: <strong style={{ color: "var(--danger)" }}>{agent.manager_name}</strong>
                </div>
                <div ref={transferSearchRef} style={{ position: "relative" }}>
                  <input
                    autoFocus
                    value={transferSearch}
                    onChange={(e) => { setTransferSearch(e.target.value); setTransferTarget(null); }}
                    placeholder="Buscar novo proprietário..."
                    style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", background: "var(--surface-raised)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none" }}
                  />
                  {transferSearchResults && transferSearchResults.length > 0 && !transferTarget && (
                    <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", zIndex: 10 }}>
                      {transferSearchResults.filter((u) => u.id !== agent.manager_id).map((u) => (
                        <button key={u.id} onClick={() => { setTransferTarget(u); setTransferSearch(u.name); }}
                          style={{ width: "100%", textAlign: "left", padding: "8px 12px", background: "none", border: "none", color: "var(--ink)", fontSize: 13, cursor: "pointer" }}
                          onMouseEnter={e => (e.currentTarget.style.background = "var(--line)")}
                          onMouseLeave={e => (e.currentTarget.style.background = "none")}
                        >{u.name}</button>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  {canManageParticipants ? (
                    <button onClick={() => { setAccessMode("choose"); setTransferSearch(""); setTransferTarget(null); }} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Voltar</button>
                  ) : (
                    <button onClick={() => setAccessMode(null)} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
                  )}
                  <button
                    onClick={() => setAccessMode("transfer-confirm")}
                    disabled={!transferTarget}
                    style={{ padding: "7px 16px", background: transferTarget ? "var(--brand)" : "var(--line)", border: "none", borderRadius: 8, color: transferTarget ? "#fff" : "var(--ink-dim)", fontSize: 13, fontWeight: 600, cursor: transferTarget ? "pointer" : "default" }}
                  >Continuar</button>
                </div>
              </>
            )}

            {/* Transfer confirm screen */}
            {accessMode === "transfer-confirm" && transferTarget && (
              <>
                <div style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.6 }}>
                  O proprietário do agente <strong style={{ color: "var(--ink)" }}>{agent.name}</strong> será trocado de{" "}
                  <strong style={{ color: "var(--danger)" }}>{agent.manager_name}</strong> para{" "}
                  <strong style={{ color: "var(--ok)" }}>{transferTarget.name}</strong>.
                </div>
                <div style={{ background: "var(--surface-raised)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "var(--ink-muted)", lineHeight: 1.5 }}>
                  Se houver uma conexão WABA Carmillion vinculada a este agente, ela também será transferida para o novo proprietário.
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button onClick={() => setAccessMode("transfer-search")} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Voltar</button>
                  <button
                    onClick={() => transferOwnerMutation.mutate(transferTarget.id)}
                    disabled={transferOwnerMutation.isPending}
                    style={{ padding: "7px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: transferOwnerMutation.isPending ? 0.6 : 1 }}
                  >
                    {transferOwnerMutation.isPending ? "Transferindo..." : "Confirmar troca"}
                  </button>
                </div>
                {transferOwnerMutation.isError && (
                  <span style={{ fontSize: 12, color: "var(--danger)" }}>
                    {(transferOwnerMutation.error as any)?.payload?.detail ?? "Erro ao transferir. Tente novamente."}
                  </span>
                )}
              </>
            )}

          </DialogPanel>
        </div>
      )}

      {/* Tag popup */}
      {showTagPopup && (() => {
        const currentTags: string[] = agent.tags ?? [];
        const q = tagSearch.trim().toLowerCase();
        const suggestions = allTags
          .map(t => t.tag)
          .filter(t => !currentTags.includes(t) && (!q || t.toLowerCase().includes(q)));
        const canCreate = q.length >= 1 && /^[a-zA-Z0-9_-]+$/.test(q) && !currentTags.includes(q) && !suggestions.includes(q);

        function removeTag(tag: string) {
          updateTagsMutation.mutate(currentTags.filter(t => t !== tag));
        }
        function addTag(tag: string) {
          if (!currentTags.includes(tag)) updateTagsMutation.mutate([...currentTags, tag]);
          setTagSearch("");
        }

        return (
          <div {...backdropClose(() => { setShowTagPopup(false); setTagSearch(""); })}
            style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
            <DialogPanel onClose={() => { setShowTagPopup(false); setTagSearch(""); }} label="Tags do agente" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 360, display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600, fontSize: 15 }}>Tags do agente</span>
                <button onClick={() => { setShowTagPopup(false); setTagSearch(""); }} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
              </div>

              {/* Current tags */}
              {currentTags.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {currentTags.map(tag => (
                    <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, padding: "3px 10px", borderRadius: 99, background: "var(--brand-soft)", color: "var(--brand)", border: "1px solid var(--brand)" }}>
                      {tag}
                      <button onClick={() => removeTag(tag)} style={{ background: "none", border: "none", color: "var(--brand)", cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                </div>
              ) : (
                <span style={{ fontSize: 12, color: "var(--ink-dim)" }}>Nenhuma tag ainda.</span>
              )}

              <div style={{ borderTop: "1px solid var(--line)" }} />

              {/* Search + create */}
              <div style={{ position: "relative" }}>
                <input
                  autoFocus
                  value={tagSearch}
                  onChange={e => setTagSearch(e.target.value.replace(/\s/g, ""))}
                  placeholder="Buscar ou criar tag..."
                  style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", background: "var(--surface-raised)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none" }}
                />
                {(suggestions.length > 0 || canCreate) && (
                  <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", zIndex: 10, maxHeight: 180, overflowY: "auto" }}>
                    {canCreate && (
                      <button onClick={() => addTag(q)}
                        style={{ width: "100%", textAlign: "left", padding: "8px 12px", background: "none", border: "none", color: "var(--brand)", fontSize: 13, cursor: "pointer", borderBottom: suggestions.length ? "1px solid var(--line)" : "none" }}
                        onMouseEnter={e => (e.currentTarget.style.background = "var(--danger-soft)")}
                        onMouseLeave={e => (e.currentTarget.style.background = "none")}
                      >+ Criar tag "{q}"</button>
                    )}
                    {suggestions.map(tag => (
                      <button key={tag} onClick={() => addTag(tag)}
                        style={{ width: "100%", textAlign: "left", padding: "8px 12px", background: "none", border: "none", color: "var(--ink)", fontSize: 13, cursor: "pointer" }}
                        onMouseEnter={e => (e.currentTarget.style.background = "var(--line)")}
                        onMouseLeave={e => (e.currentTarget.style.background = "none")}
                      >{tag}</button>
                    ))}
                  </div>
                )}
              </div>
            </DialogPanel>
          </div>
        );
      })()}

      {/* Aviso de presença: modal bloqueante — só some clicando no X/entendi */}
      {presenceOthers.length > 0 && !presenceDismissed && (
        <div style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <div role="alertdialog" aria-label="Agente aberto em outro lugar" style={{ position: "relative", background: "var(--surface-hover)", border: "1px solid var(--warn-line)", borderRadius: 14, padding: "36px 40px", width: 520, maxWidth: "calc(100vw - 48px)", display: "flex", flexDirection: "column", alignItems: "center", gap: 16, textAlign: "center" }}>
            <button onClick={() => setPresenceDismissed(true)} aria-label="Fechar aviso" style={{ position: "absolute", top: 12, right: 12, background: "none", border: "none", color: "var(--ink-secondary)", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 6 }}>✕</button>
            <span aria-hidden style={{ fontSize: 44 }}>⚠️</span>
            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)" }}>
              {presenceOthers.some((o) => !o.same_user)
                ? `Este agente está aberto por ${[...new Set(presenceOthers.filter((o) => !o.same_user).map((o) => o.name))].join(", ")}${presenceOthers.some((o) => o.same_user) ? " e em outra aba sua" : ""}`
                : "Este agente está aberto em outra aba"}
            </div>
            <div style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.6 }}>
              Alterações salvas em outro lugar podem sobrescrever as suas — a última gravação vence. Feche as outras abas antes de editar por aqui.
            </div>
            <button onClick={() => setPresenceDismissed(true)} style={{ padding: "9px 22px", background: "var(--warn)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Entendi, continuar mesmo assim
            </button>
          </div>
        </div>
      )}

      {/* Topbar */}
      <div style={{ height: 52, borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", padding: "0 24px", gap: 12, flexShrink: 0 }}>
        <a
          href="/agents"
          onClick={(e) => { e.preventDefault(); guardCtl.current.attemptLeave(() => navigate({ to: "/agents" })); }}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, border: "1px solid var(--line-strong)", borderRadius: 8, background: "var(--surface-hover)", color: "var(--ink-secondary)", textDecoration: "none", fontSize: 14, flexShrink: 0 }}
        >
          ←
        </a>
        <h1 style={{ fontWeight: 600, fontSize: 15, color: "var(--ink)", flexShrink: 0, margin: 0 }}>{agent.name}</h1>

        {/* Tag owner */}
        <span style={{
          fontSize: 11, padding: "2px 8px", borderRadius: 99,
          background: "var(--brand-soft)", color: "var(--danger)", border: "1px solid var(--danger-line)",
          flexShrink: 0,
        }}>
          Owner: {abbrevName(agent.manager_name)}
        </span>

        {/* Tags participantes */}
        {(agent.participants ?? []).map((p: { id: string; name: string }) => (
          <span key={p.id} style={{
            fontSize: 11, padding: "2px 8px", borderRadius: 99,
            background: "var(--warn-soft)", color: "var(--warn)", border: "1px solid var(--warn-line)",
            flexShrink: 0,
          }}>
            {abbrevName(p.name)}
          </span>
        ))}

        {/* Botão gerenciar acessos */}
        {(canManageParticipants || canChangeOwner) && (
          <button
            onClick={() => {
              if (canManageParticipants && canChangeOwner) setAccessMode("choose");
              else if (canManageParticipants) setAccessMode("participants");
              else setAccessMode("transfer-search");
            }}
            title="Gerenciar acessos"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 28, border: "1px solid var(--line-strong)", borderRadius: 8,
              background: "var(--surface-hover)", color: "var(--ink-secondary)", cursor: "pointer", fontSize: 14,
              flexShrink: 0,
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
            </svg>
          </button>
        )}

        {/* Botão tags */}
        {!isViewer && (
        <button
          onClick={() => setShowTagPopup(true)}
          title="Gerenciar tags"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 28, height: 28, border: "1px solid var(--line-strong)", borderRadius: 8,
            background: "var(--surface-hover)", color: "var(--ink-secondary)", cursor: "pointer", fontSize: 13,
            flexShrink: 0,
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.59 13.41 12 22l-9-9V3h10l8.59 8.59a2 2 0 0 1 0 2.82Z" transform="translate(0 -1)" />
            <circle cx="7.5" cy="7.5" r="1.5" fill="currentColor" stroke="none" />
          </svg>
        </button>
        )}

        {/* Spacer */}
        <span style={{ flex: 1 }} />

        {/* Chip de versão publicada */}
        {publishedVersion && (
          <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--surface-card)", color: "var(--ink-muted)", border: "1px solid var(--line)" }}>
            v{publishedVersion}
          </span>
        )}

        {/* Tipo (Self-Service) */}
        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: agent.template_id ? "var(--warn-soft)" : "var(--surface-card)", color: agent.template_id ? "var(--warn)" : "var(--ink-muted)", border: `1px solid ${agent.template_id ? "var(--warn-line)" : "var(--line)"}` }}>
          {agent.template_id ? `Simplificado · ${agent.global_form?.name ?? "global"}` : "Completo"}
        </span>

        {/* Badge de status */}
        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: styleStatus.bg, color: styleStatus.color, border: `1px solid ${styleStatus.border}` }}>
          {STATUS_LABEL[status] ?? status}
        </span>

        {/* Botões de ação */}
        {!isViewer && (<>
        {status === "development" && (
          <>
            <button onClick={() => setConfirm("deactivate")} style={{ padding: "5px 14px", background: "var(--line)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-mid)", fontSize: 12, cursor: "pointer" }}>
              Desativar
            </button>
            <button onClick={() => setConfirm("publish")} style={{ padding: "5px 14px", background: "var(--ok-soft)", border: "1px solid var(--ok-line)", borderRadius: 8, color: "var(--ok)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              Publicar
            </button>
          </>
        )}
        {status === "active" && (
          <>
            {dirty && (
              <button onClick={() => setShowChangelog(true)} className="publish-pulse" style={{ padding: "5px 14px", background: "var(--warn-soft)", border: "1px solid var(--warn-line)", borderRadius: 8, color: "var(--warn)", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                <AlertTriangleIcon size={12} /> Publicar
              </button>
            )}
            <button onClick={() => setConfirm("deactivate")} style={{ padding: "5px 14px", background: "var(--line)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-mid)", fontSize: 12, cursor: "pointer" }}>
              Desativar
            </button>
          </>
        )}
        {status === "inactive" && (
          <>
            {dirty && (
              <button onClick={() => setShowChangelog(true)} className="publish-pulse" style={{ padding: "5px 14px", background: "var(--warn-soft)", border: "1px solid var(--warn-line)", borderRadius: 8, color: "var(--warn)", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                <AlertTriangleIcon size={12} /> Publicar
              </button>
            )}
            <button onClick={() => setConfirm("reactivate")} style={{ padding: "5px 14px", background: "var(--ok-soft)", border: "1px solid var(--ok-line)", borderRadius: 8, color: "var(--ok)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              Reativar
            </button>
          </>
        )}
        </>)}

        {/* Excluir — só para quem tem permissão */}
        {canDelete && (
          <button onClick={() => setDeleteStep(1)} style={{ padding: "5px 14px", background: "transparent", border: "1px solid var(--brand-line)", borderRadius: 8, color: "var(--brand)", fontSize: 12, cursor: "pointer" }}>
            Excluir
          </button>
        )}
      </div>

      {/* Horizontal tabs */}
      <div
        style={{
          padding: "0 24px",
          borderBottom: "1px solid var(--line)",
          display: "flex",
          gap: 0,
          flexShrink: 0,
        }}
      >
        {visibleTabs.map((t) => {
          const isActive = t.id === activeTab;
          return (
            <button
              key={t.id}
              onClick={() => { if (t.id !== activeTab) guardCtl.current.attemptLeave(() => setActiveTab(t.id)); }}
              style={{
                padding: "10px 16px",
                fontSize: 13,
                color: isActive ? "var(--brand)" : "var(--ink-muted)",
                background: "none",
                border: "none",
                borderBottom: `2px solid ${isActive ? "var(--brand)" : "transparent"}`,
                marginBottom: -1,
                cursor: "pointer",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0, height: "100%" }}>
        {activeTab === "geral" && <TabGeral agent={agent} />}
        {activeTab === "metricas" && <TabMetricas agent={agent} />}
        {activeTab === "conversas" && <TabConversas agent={agent} />}
        {activeTab === "execucoes" && <TabExecucoes agent={agent} />}
        {activeTab === "conexao" && <TabConexao agent={agent} />}
        {activeTab === "playground" && <TabPlayground agent={agent} />}
        {activeTab === "configuracoes" && <TabConfig agent={agent} />}
        {activeTab === "insights" && <TabInsights agent={agent} />}
        {activeTab === "importn8n" && <TabImportN8n agent={agent} />}
      </div>
    </div>
    </UnsavedGuard>
  );
}

